/**
 * Universal Form Abandonment & Field Hesitation Tracking Engine
 *
 * Tracks the complete form lifecycle:
 * - Start (first focus/input)
 * - Field dwell times (hesitation before input)
 * - Validation error occurrences
 * - Form Abandonment (backed out, cancelled, closed dialog, or navigated away without submitting)
 * - Submission success and failure
 */

import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';
import { captureEvent, captureFormAbandoned, captureFormSubmitted } from './posthog';

export interface UseFormTrackerOptions {
  formId: string;
  formName: string;
  totalFields?: number;
  projectSlug?: string;
}

export interface FormTracker {
  onFieldFocus: (fieldName: string) => void;
  onFieldBlur: (fieldName: string) => void;
  onFieldChange: (fieldName: string, value?: unknown) => void;
  onValidationError: (fieldName: string, errorMessage: string) => void;
  trackSubmit: (formDataSummary?: Record<string, unknown>) => void;
  trackSubmitSuccess: (extraProps?: Record<string, unknown>) => void;
  trackSubmitError: (errorCode: string, errorMessage: string) => void;
  trackCancel: (reason?: string) => void;
}

const HESITATION_THRESHOLD_MS = 5000;

export function useFormTracker({
  formId,
  formName,
  totalFields = 1,
  projectSlug,
}: UseFormTrackerOptions): FormTracker {
  const location = useLocation();
  const screenPath = location.pathname;

  const startTimeRef = useRef<number>(Date.now());
  const hasStartedRef = useRef<boolean>(false);
  const isSubmittedRef = useRef<boolean>(false);
  const isCancelledRef = useRef<boolean>(false);

  const touchedFieldsRef = useRef<Set<string>>(new Set());
  const filledFieldsRef = useRef<Set<string>>(new Set());
  const lastFieldInteractedRef = useRef<string | null>(null);
  const validationErrorsRef = useRef<Record<string, string>>({});

  const fieldFocusTimeRef = useRef<Record<string, number>>({});
  const fieldDwellTimeRef = useRef<Record<string, number>>({});

  const ensureStarted = useCallback(() => {
    if (!hasStartedRef.current) {
      hasStartedRef.current = true;
      startTimeRef.current = Date.now();
      captureEvent('form_started', {
        form_id: formId,
        form_name: formName,
        screen_path: screenPath,
        project_slug: projectSlug,
      });
    }
  }, [formId, formName, screenPath, projectSlug]);

  const onFieldFocus = useCallback(
    (fieldName: string) => {
      ensureStarted();
      touchedFieldsRef.current.add(fieldName);
      lastFieldInteractedRef.current = fieldName;
      fieldFocusTimeRef.current[fieldName] = Date.now();

      captureEvent('form_field_focused', {
        form_id: formId,
        form_name: formName,
        field_name: fieldName,
        screen_path: screenPath,
        project_slug: projectSlug,
      });
    },
    [ensureStarted, formId, formName, screenPath, projectSlug],
  );

  const onFieldBlur = useCallback(
    (fieldName: string) => {
      const focusTime = fieldFocusTimeRef.current[fieldName];
      if (focusTime) {
        const dwell = Date.now() - focusTime;
        fieldDwellTimeRef.current[fieldName] = (fieldDwellTimeRef.current[fieldName] || 0) + dwell;

        if (dwell > HESITATION_THRESHOLD_MS) {
          captureEvent('form_field_hesitated', {
            form_id: formId,
            form_name: formName,
            field_name: fieldName,
            dwell_time_ms: dwell,
            screen_path: screenPath,
            project_slug: projectSlug,
          });
        }
      }
    },
    [formId, formName, screenPath, projectSlug],
  );

  const onFieldChange = useCallback(
    (fieldName: string, value?: unknown) => {
      ensureStarted();
      touchedFieldsRef.current.add(fieldName);
      lastFieldInteractedRef.current = fieldName;

      const isFilled =
        value !== undefined &&
        value !== null &&
        value !== '' &&
        (Array.isArray(value) ? value.length > 0 : true);

      if (isFilled) {
        filledFieldsRef.current.add(fieldName);
      } else {
        filledFieldsRef.current.delete(fieldName);
      }

      // Clear any prior validation error for this field
      delete validationErrorsRef.current[fieldName];
    },
    [ensureStarted],
  );

  const onValidationError = useCallback(
    (fieldName: string, errorMessage: string) => {
      ensureStarted();
      validationErrorsRef.current[fieldName] = errorMessage;

      captureEvent('form_validation_failed', {
        form_id: formId,
        form_name: formName,
        field_name: fieldName,
        error_message: errorMessage,
        screen_path: screenPath,
        project_slug: projectSlug,
      });
    },
    [ensureStarted, formId, formName, screenPath, projectSlug],
  );

  const trackSubmit = useCallback(
    (formDataSummary?: Record<string, unknown>) => {
      isSubmittedRef.current = true;
      const duration = Date.now() - startTimeRef.current;
      const touchedList = Array.from(touchedFieldsRef.current);

      captureFormSubmitted({
        form_id: formId,
        form_name: formName,
        time_spent_ms: duration,
        fields_touched_count: touchedList.length,
        fields_filled_count: filledFieldsRef.current.size,
        touched_fields: touchedList,
        screen_path: screenPath,
        project_slug: projectSlug,
        ...formDataSummary,
      });
    },
    [formId, formName, screenPath, projectSlug],
  );

  const trackSubmitSuccess = useCallback(
    (extraProps?: Record<string, unknown>) => {
      const duration = Date.now() - startTimeRef.current;
      captureEvent('form_submit_success', {
        form_id: formId,
        form_name: formName,
        time_spent_ms: duration,
        screen_path: screenPath,
        project_slug: projectSlug,
        ...extraProps,
      });
    },
    [formId, formName, screenPath, projectSlug],
  );

  const trackSubmitError = useCallback(
    (errorCode: string, errorMessage: string) => {
      const duration = Date.now() - startTimeRef.current;
      captureEvent('form_submit_error', {
        form_id: formId,
        form_name: formName,
        time_spent_ms: duration,
        error_code: errorCode,
        error_message: errorMessage,
        screen_path: screenPath,
        project_slug: projectSlug,
      });
    },
    [formId, formName, screenPath, projectSlug],
  );

  const trackCancel = useCallback(
    (reason = 'user_cancelled') => {
      isCancelledRef.current = true;
      if (hasStartedRef.current && !isSubmittedRef.current && touchedFieldsRef.current.size > 0) {
        const duration = Date.now() - startTimeRef.current;
        const touchedList = Array.from(touchedFieldsRef.current);
        const completionPct = Math.round(
          (filledFieldsRef.current.size / Math.max(1, totalFields)) * 100,
        );

        captureFormAbandoned({
          form_id: formId,
          form_name: formName,
          time_spent_ms: duration,
          fields_touched_count: touchedList.length,
          fields_filled_count: filledFieldsRef.current.size,
          touched_fields: touchedList,
          last_field_interacted: lastFieldInteractedRef.current,
          had_validation_errors: Object.keys(validationErrorsRef.current).length > 0,
          validation_errors: { ...validationErrorsRef.current, cancel_reason: reason },
          completion_percentage: completionPct,
          screen_path: screenPath,
          project_slug: projectSlug,
        });
      }
    },
    [formId, formName, totalFields, screenPath, projectSlug],
  );

  // Detect unmount abandonment (e.g. navigated away, closed modal, tab switch)
  useEffect(() => {
    return () => {
      if (
        hasStartedRef.current &&
        !isSubmittedRef.current &&
        !isCancelledRef.current &&
        touchedFieldsRef.current.size > 0
      ) {
        const duration = Date.now() - startTimeRef.current;
        const touchedList = Array.from(touchedFieldsRef.current);
        const completionPct = Math.round(
          (filledFieldsRef.current.size / Math.max(1, totalFields)) * 100,
        );

        captureFormAbandoned({
          form_id: formId,
          form_name: formName,
          time_spent_ms: duration,
          fields_touched_count: touchedList.length,
          fields_filled_count: filledFieldsRef.current.size,
          touched_fields: touchedList,
          last_field_interacted: lastFieldInteractedRef.current,
          had_validation_errors: Object.keys(validationErrorsRef.current).length > 0,
          validation_errors: validationErrorsRef.current,
          completion_percentage: completionPct,
          screen_path: screenPath,
          project_slug: projectSlug,
        });
      }
    };
  }, [formId, formName, totalFields, screenPath, projectSlug]);

  return {
    onFieldFocus,
    onFieldBlur,
    onFieldChange,
    onValidationError,
    trackSubmit,
    trackSubmitSuccess,
    trackSubmitError,
    trackCancel,
  };
}
